import * as React from 'react'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { match, useHistory, useRouteMatch } from 'react-router-dom'
import { Divider, makeStyles, MessageBar, MessageBarBody, tokens, Tooltip } from '@fluentui/react-components'

// @ts-expect-error("dompurify exports differ from what ts-lint is expecting and cannot see the default export")
import DOMPurify from 'dompurify'

import Loading from '../../../components/Loading'
import useEntity from '../../../hooks/useEntity'
import { IFileDto } from '../../../config/fileConfig'
import { MainDto } from '../../../config/filePartyConfig'
import { Route, Switch } from 'react-router'
import Tasks from '../../../pages/Tasks'
import ViewFileHeader from './ViewFileHeader'
import FinanceMonitoringOverview from '../../../pages/File/FinanceMonitoringOverview'
import AuditTrailOverview from '../../../pages/File/AuditTrailOverview'
import ViewFileTabs, { attributeCategoryTabKey, ToolbarItemKey } from './ViewFileTabs'
import Times from '../../../pages/Times'
import { ModuleType } from '../../../context/GlobalContext'
import useModules from '../../../hooks/module/useModules'
import Workflows from '../../../pages/Workflows'
import FileDocumentOverview from './ViewFileTabs/FileDocumentOverview'
import { AppAction, IBackgroundLocationState } from '../../../config/appConfig'
import { useDispatch } from 'react-redux'
import { MenuItemKey } from '../../../components/MainMenu'
import FileEmailOverview from './ViewFileTabs/FileEmailOverview'
import { FolderDriveItem } from '../../../components/Document/DocumentFlatTree'
import AddNaturalPerson from '../../../pages/Party/AddNaturalPerson'
import EditNaturalPerson from '../../../pages/Party/EditNaturalPerson'
import AddLegalPerson from '../../../pages/Party/AddLegalPerson'
import EditLegalPerson from '../../../pages/Party/EditLegalPerson'
import FilePartyOverview from '../../../pages/Party/FilePartyOverview'
import TravelOverview from '../../../pages/File/Registration/TravelOverview'
import DisbursementOverview from '../../../pages/File/Registration/DisbursementOverview'
import InjuryOverview from '../../../pages/File/Registration/InjuryOverview'
import FamilyLawOverview from '../../../pages/File/Registration/FamilyLawOverview'
import { LegalAidCertificateOverview } from '../../../pages/File/Registration/LegalAidCertificateOverview'
import { DocketProceedingOverview } from '../../../pages/File/Registration/DocketProceedingOverview'
import Notes from '../../../pages/Notes'
import { MutationRouteMatchProps } from '../../../pages/BaseMutableOverview/BaseMutableOverview'
import { useApplicationStyles } from '../../../pages/App'
import OldFixedPriceOverview from '../../../pages/File/Registration/OldFixedPriceOverview'
import useAttributes, { AttributeContextType, AttributeDefinitionDto, AttributeViewType, EntityAttributeType } from '../../../hooks/useAttributes'
import { useTranslation } from 'react-i18next'
import AttributesOverview from '../../../pages/File/AttributesOverview'

export const fileRoutePrefix = '/file/:fileId'

export const usePageStyles = makeStyles({
    page: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    headerCard: {
        flexShrink: 0,
    },
    toolbar: {
        minWidth: 0,
        minHeight: 0,
        flexGrow: 1,
        padding: 0,
    },
    toolbarCard: {
        backgroundColor: tokens.colorBrandBackground2,
        minHeight: '32px',
        boxSizing: 'content-box',
    },
    toolbarTabs: {
        paddingLeft: 0,
    },
})

export type FileRouteMatchProps = { fileId: string; tab?: ToolbarItemKey } & MutationRouteMatchProps

export type ViewFileHeaderProps = {
    fileId: string

    file: IFileDto
    resetFile: () => void

    parties: MainDto[]
    resetParties: () => void

    selectedDriveItemIds: string[]
    setSelectedDriveItemIds: (items: string[]) => void
    selectedDriveItems: FolderDriveItem[]
    setSelectedDriveItems: (items: FolderDriveItem[]) => void
}

export type ViewFileProps = ViewFileHeaderProps & {
    routeMatch: match<FileRouteMatchProps>
}

export default function ViewFile(): ReactElement {
    const appStyles = useApplicationStyles()
    const dispatch = useDispatch()
    const history = useHistory<IBackgroundLocationState>()
    const modules = useModules()
    const pageStyles = usePageStyles()
    const { t: translate } = useTranslation()

    const routeMatch = useRouteMatch<FileRouteMatchProps>()
    const fileId = routeMatch.params?.fileId

    // redirect to document tab when no tab was selected
    if (history?.location?.pathname === `/file/${fileId}`) {
        history.push(`/file/${fileId}/${ToolbarItemKey.Document}`)
    }

    useEffect(() => {
        dispatch({ type: AppAction.SetBreadcrumbs, payload: [MenuItemKey.Files] })
    }, [])

    const [file, , resetFile] = useEntity<IFileDto>(() => ({ url: `/file/${fileId}` }), fileId || false)
    const [parties, , resetParties] = useEntity<MainDto[]>(() => ({ url: `/file/${fileId}/party` }), fileId || false)

    const [selectedDriveItemIds, setSelectedDriveItemIds] = useState<string[]>([])
    const [selectedDriveItems, setSelectedDriveItems] = useState<FolderDriveItem[]>([])

    const { activeAttributeDefinitions } = useAttributes(
        AttributeViewType.View,
        [AttributeContextType.File],
        undefined,
        EntityAttributeType.File,
        file.data,
    )

    const headerProps = useMemo<ViewFileHeaderProps>(
        () => ({
            fileId,
            file: file.data,
            resetFile,
            parties: parties.data,
            resetParties,
            selectedDriveItemIds,
            setSelectedDriveItemIds,
            selectedDriveItems,
            setSelectedDriveItems,
        }),
        [file.loaded, parties.loaded, selectedDriveItemIds, selectedDriveItems],
    )

    const componentProps = useMemo<ViewFileProps>(() => ({ ...headerProps, routeMatch }), [headerProps, routeMatch])
    const fileNotification = useMemo<string>(
        () => DOMPurify.sanitize(file.data?.notification || '')?.replaceAll('\n', '<br/>'),
        [file.data, file.data?.notification],
    )

    const groupedAttributeDefinitions = useMemo<Map<string, AttributeDefinitionDto[]>>(() => {
        return activeAttributeDefinitions?.reduce((previousValue, currentValue) => {
            const category = currentValue.category || translate('file.pivot.attribute.title')
            const prevValues = previousValue.get(category) || []
            previousValue.set(category, [...prevValues, currentValue])
            return previousValue
        }, new Map<string, AttributeDefinitionDto[]>())
    }, [activeAttributeDefinitions])

    if (!file.loaded) {
        return <Loading />
    }

    return (
        <div className={pageStyles.page}>
            <ViewFileHeader {...headerProps} />
            {fileNotification && (
                <Tooltip
                    relationship={'description'}
                    content={
                        <div
                            dangerouslySetInnerHTML={{
                                __html: fileNotification,
                            }}
                        ></div>
                    }
                    withArrow
                >
                    <MessageBar intent="warning" layout={'multiline'} className={appStyles.minHeight}>
                        <MessageBarBody
                            style={{
                                //chromium
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                //Safari/Firefox
                                lineClamp: 3,
                            }}
                        >
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: fileNotification,
                                }}
                            ></div>
                        </MessageBarBody>
                    </MessageBar>
                </Tooltip>
            )}
            <ViewFileTabs {...componentProps} />
            <Divider />
            <Switch>
                {/*We cannot change the file ID param to :id, because it can encapsulate any route that already uses // the :id param for its own entity.*/}
                <Route path={`${fileRoutePrefix}/${ToolbarItemKey.Email}`} render={() => <FileEmailOverview {...componentProps} />} />
                <Route path={`${fileRoutePrefix}/${ToolbarItemKey.Document}`} render={() => <FileDocumentOverview {...componentProps} />} />
                {modules.has(ModuleType.Tasks) && (
                    <Route
                        path={`${fileRoutePrefix}/${ToolbarItemKey.Task}/:action?`}
                        render={() => <Tasks routePrefix={fileRoutePrefix} currentFile={file.data} />}
                    />
                )}
                {modules.has(ModuleType.Workflow) && (
                    <Route
                        path={`${fileRoutePrefix}/${ToolbarItemKey.Workflow}/:action?`}
                        render={() => <Workflows routePrefix={fileRoutePrefix} currentFile={file.data} />}
                    />
                )}
                {modules.has(ModuleType.TimeTracking) && (
                    <Route
                        path={`${fileRoutePrefix}/${ToolbarItemKey.Time}/:action?`}
                        render={() => <Times routePrefix={fileRoutePrefix} currentFile={file.data} />}
                    />
                )}
                {modules.has(ModuleType.Attributes) &&
                    Array.from(groupedAttributeDefinitions.entries()).map(([key, attributeDefs]) => (
                        <Route
                            key={key}
                            path={`${fileRoutePrefix}/${attributeCategoryTabKey(key)}`}
                            render={() => <AttributesOverview entity={file} category={key} attributeDefinitions={attributeDefs} />}
                        />
                    ))}
                {modules.has(ModuleType.Invoices) && (
                    <Route path={`${fileRoutePrefix}/${ToolbarItemKey.Monitoring}`} render={() => <FinanceMonitoringOverview entity={file} />} />
                )}
                <Route path={`${fileRoutePrefix}/${ToolbarItemKey.AuditTrail}`} render={() => <AuditTrailOverview entity={file} />} />

                {/*Temporary routing for old pages, will be replaced when new pages are created */}
                <Route path={`${fileRoutePrefix}/party/natural/add`} component={AddNaturalPerson} />
                <Route path={`${fileRoutePrefix}/party/natural/:id/edit`} component={EditNaturalPerson} />
                <Route path={`${fileRoutePrefix}/party/legal/add`} component={AddLegalPerson} />
                <Route path={`${fileRoutePrefix}/party/legal/:id/edit`} component={EditLegalPerson} />
                <Route
                    path={`${fileRoutePrefix}/party`}
                    render={() => <FilePartyOverview data={parties.data} documents={null} file={file.data} reset={resetParties} />}
                />
                {modules.has(ModuleType.TravelExpenses) && (
                    <Route
                        path={`${fileRoutePrefix}/${ToolbarItemKey.Travel}`}
                        render={() => <TravelOverview listProps={{ file: file.data }} items={null} reset={null} />}
                    />
                )}
                {modules.has(ModuleType.Disbursements) && (
                    <Route
                        path={`${fileRoutePrefix}/${ToolbarItemKey.Disbursement}`}
                        render={() => <DisbursementOverview listProps={{ file: file.data }} items={null} reset={null} />}
                    />
                )}
                {modules.has(ModuleType.FixedPrices) && (
                    <Route
                        path={`${fileRoutePrefix}/${ToolbarItemKey.FixedPrice}`}
                        render={() => <OldFixedPriceOverview listProps={{ file: file.data }} items={null} reset={null} />}
                    />
                )}
                {modules.has(ModuleType.Injury) && <Route path={`${fileRoutePrefix}/${ToolbarItemKey.Injury}`} render={() => <InjuryOverview />} />}
                {modules.has(ModuleType.FamilyLaw) && (
                    <Route
                        path={`${fileRoutePrefix}/${ToolbarItemKey.FamilyLaw}`}
                        render={() => <FamilyLawOverview file={file} resetFile={resetFile} />}
                    />
                )}
                {modules.has(ModuleType.LegalAid) && (
                    <Route
                        path={`${fileRoutePrefix}/${ToolbarItemKey.LegalAidCertificate}`}
                        render={() => <LegalAidCertificateOverview file={file} parties={parties} certificates={null} reset={null} />}
                    />
                )}
                {modules.has(ModuleType.Docket) && (
                    <Route
                        path={`${fileRoutePrefix}/${ToolbarItemKey.Docket}`}
                        render={() => <DocketProceedingOverview file={file} parties={parties} docketProceedings={null} reset={null} />}
                    />
                )}
                <Notes overviewProps={{ notes: null, file: file.data, reset: null }} routePrefix={fileRoutePrefix} />
            </Switch>
        </div>
    )
}
